/**
 * YearInReviewExport — Premium feature (Explorer Pro+).
 * One-click MP4 generator: backend renders a 1080x1920 vertical video of
 * the user's completed trips for a chosen year. Result streamed back via
 * /api/premium/year-in-review/file/{id} so the user can preview, download,
 * and share to socials.
 *
 * Free users see a teaser/upsell in the same dialog; the Generate button
 * is replaced with an Upgrade CTA that opens the existing PaywallScreen.
 * Backend route is also gated via enforce_feature("year_in_review") so
 * direct API calls from a free account return 403.
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Film, Download, Share2, Loader2, Sparkles, Lock } from "lucide-react";
import { useEntitlements } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

const CURRENT_YEAR = new Date().getFullYear();

function YearInReviewExport({ isOpen, onClose }) {
  const { token } = useAuth();
  const { hasUniversalAccess, hasPaidAccess } = useEntitlements();
  const hasAccess = hasUniversalAccess || hasPaidAccess;
  const [showPaywall, setShowPaywall] = useState(false);
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [rendering, setRendering] = useState(false);
  const [renderProgress, setRenderProgress] = useState(""); // status message
  const [latest, setLatest] = useState(null); // { id, year, storage_path, size_bytes, ... }
  const [previousExports, setPreviousExports] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  // Year picker — derived from user's actual completed-trip years.
  // Falls back to last 6 years until the trip fetch resolves so the
  // dropdown is never empty.
  const [tripYears, setTripYears] = useState([]);

  // Build the year option list. Combine:
  //   * the user's actual trip years (so 2001 + 2019 show even though they're
  //     >5 years old)
  //   * a recent window (current year + last 5) so the user can still select
  //     a year they have no trips in (helpful error message comes from backend)
  // Then sort newest-first and dedupe.
  const yearOptions = useMemo(() => {
    const recent = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);
    const all = new Set([...recent, ...tripYears]);
    return Array.from(all).sort((a, b) => b - a);
  }, [tripYears]);

  useEffect(() => {
    if (!isOpen || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/premium/year-in-review/my-exports`);
        if (cancelled) return;
        const exports = res.data.exports || [];
        setPreviousExports(exports);
        if (exports[0]) {
          setLatest(exports[0]);
          setVideoUrl(buildVideoUrl(exports[0].id));
        }
      } catch (e) {
        // 403 is OK (free tier); 401 won't happen because we're inside auth
        if (e.response?.status !== 403) {
          console.debug("YIR exports fetch failed:", e.message);
        }
      }

      // Default the year picker to the most recent year the user actually
      // has completed trips in — saves the user from picking 2026 (current)
      // and getting "no completed trips for 2026". Falls back to current year.
      // Also populates `tripYears` so the dropdown lists ALL the user's
      // historical years (e.g. 2001, 2019) — not just the last 6.
      try {
        const resp = await axios.get(`${API}/items?completed=true`);
        const items = Array.isArray(resp.data) ? resp.data : (resp.data?.items || []);
        const years = items
          .map((i) => (i.target_date || i.completed_at || "").slice(0, 4))
          .filter((y) => /^\d{4}$/.test(y))
          .map((y) => parseInt(y, 10))
          // Sanity bounds — anything outside [1970, current+1] is bogus data
          .filter((y) => y >= 1970 && y <= CURRENT_YEAR + 1);
        if (!cancelled) {
          setTripYears(Array.from(new Set(years)));
          if (years.length > 0) {
            const latestYear = Math.max(...years);
            setYear(String(latestYear));
          }
        }
      } catch (e) {
        // best-effort — if it fails the user can still pick manually
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, token]);

  const buildVideoUrl = (id) => {
    // Inline blob URL is awkward (auth header needed) — instead include the
    // bearer token via a one-shot fetch + object URL.
    return `${API}/premium/year-in-review/file/${id}`;
  };

  const handleRender = async () => {
    setRendering(true);
    setRenderProgress("Queueing your year…");
    setVideoUrl(null);
    try {
      const res = await axios.post(`${API}/premium/year-in-review/render?year=${year}`);
      const job = res.data;

      // Idempotent shortcut: if backend immediately returns a finished job
      // (legacy "complete" or new "completed"), skip polling.
      if ((job.status === "completed" || job.status === "complete") && job.export_id) {
        await loadCompletedExport(job.export_id);
        return;
      }

      // Poll job status. Backend now exposes `is_terminal` so we don't
      // have to keep a list of status strings in sync. Backend also
      // auto-recovers stale rendering jobs, so the polling loop will
      // ALWAYS see a terminal status within ~5 minutes — but we cap our
      // own foreground poll at 90s and tell the user it'll continue in
      // the background after that.
      const jobId = job.id;
      const start = Date.now();
      setRenderProgress("Rendering your year… (~20–40s)");
      let finished = false;
      while (!finished && Date.now() - start < 90_000) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const statusRes = await axios.get(`${API}/premium/year-in-review/status/${jobId}`);
          const j = statusRes.data;

          // SUCCESS
          if ((j.status === "completed" || j.status === "complete") && j.export_id) {
            finished = true;
            await loadCompletedExport(j.export_id);
            toast.success("Your Year in Review is ready!");
            return;
          }

          // FAILURE / CANCEL — exit polling and surface the actual reason
          if (j.is_terminal || j.status === "failed" || j.status === "cancelled") {
            finished = true;
            const reason =
              j.failure_reason ||
              j.error ||
              (j.status === "cancelled" ? "Render was cancelled." : "Render failed. Please try again.");
            toast.error(reason);
            return;
          }
        } catch (pollErr) {
          // 5xx / 404 — log once but keep polling for transient blips
          console.debug("yir poll error", pollErr?.message);
        }
      }
      if (!finished) {
        toast.info("Still rendering — we'll send a push when it's ready.");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to start Year in Review render";
      if (e.response?.status === 403) {
        toast.error("This is a premium feature — upgrade to export.");
      } else if (typeof msg === "string" && msg.includes("No completed trips")) {
        toast.error(`No completed trips for ${year}. Mark some trips as complete first.`);
      } else {
        toast.error(msg);
      }
    } finally {
      setRendering(false);
      setRenderProgress("");
    }
  };

  const loadCompletedExport = async (exportId) => {
    try {
      // Refresh export list so card data is accurate
      const res = await axios.get(`${API}/premium/year-in-review/my-exports`);
      const exports = res.data.exports || [];
      setPreviousExports(exports);
      const found = exports.find((e) => e.id === exportId) || { id: exportId };
      setLatest(found);
      await fetchAsBlobUrl(exportId);
    } catch (e) {
      console.debug("Reload exports failed:", e.message);
    }
  };

  const fetchAsBlobUrl = async (id) => {
    try {
      const resp = await axios.get(`${API}/premium/year-in-review/file/${id}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      setVideoUrl(url);
      return { url, blob: resp.data };
    } catch (e) {
      toast.error("Could not load the video preview");
      return null;
    }
  };

  // Read a Blob as a base64 data URL — used to hand the MP4 to the native
  // shell over postMessage (WKWebView has no <a download> support).
  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onloadend = () => {
        // result is "data:video/mp4;base64,XXXX" — split off the prefix
        const result = String(reader.result || "");
        const idx = result.indexOf(",");
        resolve(idx >= 0 ? result.slice(idx + 1) : result);
      };
      reader.readAsDataURL(blob);
    });

  // Returns true if we successfully handed off to the native shell.
  const tryNativeDownload = async (id, year) => {
    const isNative =
      typeof window !== "undefined" &&
      window.isNativeApp === true &&
      window.ReactNativeWebView &&
      typeof window.ReactNativeWebView.postMessage === "function";
    if (!isNative) return false;
    try {
      const resp = await axios.get(`${API}/premium/year-in-review/file/${id}`, {
        responseType: "blob",
      });
      const base64 = await blobToBase64(resp.data);
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "YIR_DOWNLOAD_VIDEO",
          filename: `duolyst-${year}.mp4`,
          mime: "video/mp4",
          base64,
        }),
      );
      toast.success("Opening share sheet…");
      return true;
    } catch (e) {
      console.debug("native YIR download failed:", e?.message);
      return false;
    }
  };

  const handleDownload = async () => {
    if (!latest) return;

    // Native iOS/Android wrapper — hand off to expo-sharing so the user
    // gets a real Save-to-Photos / Messages share sheet.
    if (await tryNativeDownload(latest.id, latest.year)) return;

    // Fetch the MP4 as a Blob (with our auth header).
    let blob = null;
    try {
      const resp = await axios.get(`${API}/premium/year-in-review/file/${latest.id}`, {
        responseType: "blob",
      });
      blob = resp.data;
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
    } catch (e) {
      toast.error("Couldn't fetch the video. Please try again.");
      return;
    }

    // iOS Safari (and Safari on iPadOS) IGNORES <a download> — it just
    // opens the MP4 in a new tab. The only working path is the Web Share
    // API with files, which surfaces the native iOS sheet (Save to Photos
    // / Save to Files / Messages / WhatsApp). Same path is great UX on
    // Android Chrome too. Falls back to the anchor click on desktop.
    try {
      const file = new File([blob], `duolyst-${latest.year}.mp4`, { type: "video/mp4" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `My ${latest.year} Year in Review`,
          text: `My DuoLyst Year in Review — ${latest.year}`,
        });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return; // user cancelled the sheet
      // any other share failure → fall through to anchor download
    }

    // Desktop / browsers without Web Share API → blob anchor click.
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `duolyst-${latest.year}.mp4`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke a moment later so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  const handleShare = async () => {
    if (!latest) return;

    // Native shell — same bridge as download. expo-sharing already shows a
    // share sheet on iOS so Save-to-Photos / WhatsApp / etc. are one tap.
    if (await tryNativeDownload(latest.id, latest.year)) return;

    try {
      const resp = await axios.get(`${API}/premium/year-in-review/file/${latest.id}`, {
        responseType: "blob",
      });
      const file = new File([resp.data], `duolyst-${latest.year}.mp4`, { type: "video/mp4" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `My ${latest.year} Year in Review`,
          text: `My DuoLyst Year in Review — ${latest.year} 🌍✈️`,
        });
      } else {
        handleDownload();
        toast.info("Sharing not supported here — download started instead.");
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error("Couldn't share — download started instead.");
        handleDownload();
      }
    }
  };

  const formatSize = (b) => {
    if (!b) return "";
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <>
      {showPaywall && (
        <PaywallScreen
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          type={PaywallType.FEATURE_GATE}
          feature="year_in_review"
          title="Year in Review"
          description="Render a vertical 1080×1920 video of your year in trips — perfect for Stories, Reels, and TikTok. Available with Explorer Pro."
        />
      )}
      <Dialog open={isOpen && !showPaywall} onOpenChange={onClose}>
      <DialogContent
        className="max-w-md bg-card border-none rounded-2xl"
        data-testid="yir-export-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <Film className="w-5 h-5 text-primary" />
            Year in Review — MP4 Export
          </DialogTitle>
          <DialogDescription>
            {hasAccess
              ? "Generate a vertical 1080×1920 video of your year — perfect for Stories, Reels, and TikTok."
              : "Render a vertical 1080×1920 video of your year — Explorer Pro perk."}
          </DialogDescription>
        </DialogHeader>

        {!hasAccess ? (
          /* Free-tier teaser — matches the PaywallScreen aesthetic so the
             entire app speaks one premium-upsell language. The generate
             button is replaced by an Upgrade CTA that opens the actual
             PaywallScreen with checkout flow. Backend route also enforces
             this via enforce_feature, so a direct API call from a free
             account would still return 403. */
          <div className="space-y-4" data-testid="yir-paywall-teaser">
            <div className="aspect-[9/16] max-h-[40vh] rounded-xl overflow-hidden relative bg-gradient-to-br from-primary/30 via-pink-500/20 to-purple-500/30 flex items-center justify-center">
              <div className="absolute inset-0 backdrop-blur-sm" />
              <div className="relative text-center px-6 space-y-2">
                <div className="mx-auto w-12 h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <p className="text-white font-serif text-lg">Your 2024 Year, animated.</p>
                <p className="text-white/80 text-xs">12 trips. 6 countries. 1 stunning reel.</p>
              </div>
            </div>

            <ul className="space-y-1.5 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 mt-0.5 text-primary flex-shrink-0" />
                <span>Auto-narrated highlight reel of every completed trip</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 mt-0.5 text-primary flex-shrink-0" />
                <span>1080×1920 portrait — Instagram, TikTok, and Reels-ready</span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 mt-0.5 text-primary flex-shrink-0" />
                <span>Re-render any year, any time — they never expire</span>
              </li>
            </ul>

            <Button
              onClick={() => setShowPaywall(true)}
              className="w-full bg-gradient-to-r from-primary to-pink-500 text-white rounded-xl h-11"
              data-testid="yir-upgrade-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Upgrade to Explorer Pro
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              Includes Year in Review, AI Trip Planner, Premium Insights and more.
            </p>
          </div>
        ) : (
        <div className="space-y-4">
          {/* Year picker */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground flex-shrink-0">Year</span>
            <Select value={year} onValueChange={setYear} disabled={rendering}>
              <SelectTrigger className="flex-1" data-testid="yir-year-picker">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72 overflow-y-auto">
                {yearOptions.map((y) => (
                  <SelectItem
                    key={y}
                    value={String(y)}
                    data-testid={`yir-year-${y}`}
                  >
                    {y}
                    {tripYears.includes(y) && (
                      <span className="ml-2 text-[10px] text-emerald-500">●</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Generate button */}
          <Button
            onClick={handleRender}
            disabled={rendering}
            className="w-full bg-gradient-to-r from-primary to-pink-500 text-white rounded-xl h-11"
            data-testid="yir-generate-btn"
          >
            {rendering ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {renderProgress || "Rendering your year…"}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {latest && latest.year === Number(year) ? "Re-generate" : "Generate Year in Review"}
              </>
            )}
          </Button>

          {rendering && (
            <p className="text-[11px] text-muted-foreground text-center" data-testid="yir-rendering-hint">
              You can close this dialog — we'll send a push when it's ready.
            </p>
          )}

          {/* Video preview */}
          {videoUrl && latest && (
            <div className="rounded-xl overflow-hidden bg-black aspect-[9/16] max-h-[60vh]">
              <video
                key={videoUrl}
                src={videoUrl}
                controls
                playsInline
                className="w-full h-full object-contain"
                data-testid="yir-video-preview"
              />
            </div>
          )}

          {latest && videoUrl && (
            <div className="flex gap-2">
              <Button
                onClick={handleDownload}
                variant="outline"
                className="flex-1 rounded-xl"
                data-testid="yir-download-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button
                onClick={handleShare}
                className="flex-1 bg-secondary hover:bg-secondary/90 text-white rounded-xl"
                data-testid="yir-share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          )}

          {latest && (
            <p className="text-xs text-muted-foreground text-center">
              {latest.year} · {latest.trip_count} trips · {latest.duration_seconds}s · {formatSize(latest.size_bytes)}
            </p>
          )}

          {/* Previous exports */}
          {previousExports.length > 1 && (
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Previous exports</p>
              <div className="space-y-1">
                {previousExports.slice(0, 5).map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => { setLatest(e); fetchAsBlobUrl(e.id); }}
                    className="w-full text-left text-xs px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-between"
                    data-testid={`yir-prev-${e.id}`}
                  >
                    <span className="font-medium">{e.year}</span>
                    <span className="text-muted-foreground">
                      {e.trip_count} trips · {formatSize(e.size_bytes)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tip when no exports yet for premium users */}
          {!latest && !rendering && (
            <div className="text-xs text-muted-foreground text-center bg-muted/30 rounded-lg p-3 flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary" />
              <span>
                Pick a year with completed trips and tap Generate. Your video
                will be 1080×1920 portrait — Instagram Stories ready.
              </span>
            </div>
          )}
        </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}

export default YearInReviewExport;
